import type { EndorsementType } from "@/types";

// ─────────────────────────────────────────────────────────────
// Endorsement descriptions — frontend-only registry
//
// Each type has a short description (1-liner for pickers/lists)
// and a detail description (contextual info for the detail panel).
// ─────────────────────────────────────────────────────────────

export interface EndorsementDescription {
  /** One-line summary shown in the type picker and config form header */
  short: string;
  /** Expanded description shown in the detail panel */
  detail: string;
}

export const ENDORSEMENT_DESCRIPTIONS: Record<EndorsementType, EndorsementDescription> = {
  deductible_modification: {
    short: "Adjusts individual deductibles based on qualifying medical conditions.",
    detail:
      "Allows specific claimants to receive a modified (typically reduced) deductible when they meet qualifying medical criteria such as kidney failure, cancer, transplant, or specialty drug needs. Supports approved vendor requirements, lasered claimant exclusions, and ICD/CPT code targeting.",
  },

  family_deductible: {
    short:
      "Establishes a combined deductible limit at an employee or subscriber level and covers all individuals associated with that employee (i.e. family units). ",
    detail:
      "Sets a subscriber or employee deductible that caps the total out-of-pocket exposure for a family unit. Once the family deductible is satisfied, individual members no longer need to meet their own deductible.",
  },

  tiered_deductible: {
    short: "Applies different deductible amounts based on age or tier classification.",
    detail:
      "Defines multiple deductible tiers, each with its own deductible amount, age range, and reimbursement percentage. Commonly used when plan design varies by employee age band or benefit tier. Tier data is managed as a sub-collection.",
  },

  aggregate_accommodation: {
    short:
      "Provides a portion of the anticipated Aggregate Insurance claim during the year instead of waiting until the total of claims are calculated. ",
    detail:
      "Offers advance funding when claims in aggregate surpass a defined threshold. Configurable with repayment terms, interest rates, proration, and advance restrictions during initial or final policy months.",
  },

  experience_credit: {
    short: "Provides a premium credit based on favorable claims experience.",
    detail:
      "Rewards groups with lower-than-expected claims by applying a credit rate when the loss ratio falls below a defined threshold. The credit is typically calculated as a percentage of premium based on the group's claims performance.",
  },

  experience_refund: {
    short: "Returns a portion of premium when claims are below a defined loss ratio.",
    detail:
      "Provides a refund of stop-loss premium when the group's actual loss ratio is below the minimum threshold. Supports sliding scale, flat, or tiered calculation methods with a configurable maximum refund percentage.",
  },

  rate_caps: {
    short: "Provides a maximum rate of premium increase at renewal.",
    detail:
      "Protects the policyholder from excessive premium increases by capping the maximum percentage increase that can be applied at renewal. Can be configured to apply specifically to aggregate-specific coverage.",
  },

  no_new_lasers: {
    short: "Prevents new or revised specific deductibles (lasers) from being applied on an individual claimant.",
    detail:
      "Guarantees that no new lasered deductibles will be imposed on individual claimants during the policy period or at renewal. Can also prevent existing lasers from being revised upward. Typically includes an effective date for when the protection begins.",
  },

  advance_funding: {
    short: "Accelerated claims payments to reduce employer cash flow burden.",
    detail:
      "Allows the carrier to advance a percentage of expected claims funding, with configurable minimum claim payment thresholds and subsequent payment thresholds. Helps employers avoid large, unpredictable claims disbursements.",
  },

  admin_changes: {
    short: "Tracks administrative modifications to policy terms during the contract period.",
    detail:
      "Records administrative changes such as benefit modifications, eligibility adjustments, or procedural updates that occur during the policy period. Each change entry captures the type of change and its effective date.",
  },

  leave_of_absence: {
    short: "Defines coverage continuation terms during approved leaves of absence.",
    detail:
      "Specifies how long coverage continues when an employee is on an approved leave of absence, and whether premium payments are required during the leave period. Commonly applies to FMLA, disability, or other approved leave types.",
  },

  amendments: {
    short: "Formal modifications to the original policy contract language.",
    detail:
      "Tracks numbered amendments to the policy contract that modify, add, or remove specific terms and conditions. Each amendment entry includes a sequential amendment number and the date it becomes effective.",
  },

  provider_dispute_credit: {
    short: "Applies a credit for claims under active provider payment disputes.",
    detail:
      "Issues a dollar-amount credit for claims that are being disputed with healthcare providers. Credited claim IDs are tracked as a sub-collection to maintain an audit trail of which claims are affected by the dispute.",
  },

  plan_mirroring: {
    short: "Mirrors the underlying health plan's benefit structure for stop-loss purposes.",
    detail:
      "Aligns the stop-loss contract with the underlying medical plan's benefit design, ensuring consistent treatment of covered services. Can exclude specific ICD diagnostic codes or CPT procedure codes from the mirrored coverage.",
  },

  retained_corridor: {
    short: "Defines a retention band between the deductible and reimbursement threshold.",
    detail:
      "Establishes a percentage corridor between the specific deductible and the point at which the carrier begins reimbursement. The retained percentage range creates a shared-risk band where the employer retains a portion of claims costs.",
  },

  specific_terminal_liability: {
    short: "Covers specific (individual) claims incurred during the policy but paid after termination.",
    detail:
      "Provides run-out coverage for individual claimant claims that were incurred during the policy period but are presented for payment after the policy terminates. Configurable by number of months covered, paid vs. incurred basis, and maximum benefit amount.",
  },

  aggregate_terminal_liability: {
    short: "Covers aggregate claims incurred during the policy but paid after termination.",
    detail:
      "Provides run-out coverage at the aggregate (group-level) for claims incurred during the policy period but paid after termination. Configurable by number of months covered, incurred basis, and maximum benefit amount.",
  },

  contingent_terminal_liability: {
    short: "Provides terminal liability coverage triggered by specific qualifying events.",
    detail:
      "Offers conditional terminal liability coverage that is activated only when certain trigger events occur, such as carrier insolvency or non-renewal. Trigger types and months of coverage are configurable.",
  },

  clinical_trials: {
    short: "Covers claims related to approved clinical trial participation.",
    detail:
      "Extends stop-loss coverage to include costs associated with a claimant's participation in approved clinical trials. Configurable with a per-patient maximum and specific covered trial phases. Certain ICD codes can be excluded.",
  },

  transplant_coverage: {
    short: "Provides enhanced coverage terms for organ and tissue transplant procedures.",
    detail:
      "Defines special coverage terms for transplant-related claims, including a separate deductible and lifetime maximum. Can require treatment at a designated Center of Excellence. Specific CPT procedure codes can be excluded from coverage.",
  },

  travel_medical: {
    short: "Covers medical expenses incurred while traveling outside the home network area.",
    detail:
      "Provides coverage for emergency or non-emergency medical treatment received while the member is traveling. Configurable with per-incident and annual maximum limits, and the ability to restrict to emergency-only coverage. Specific countries can be excluded.",
  },

  reference_based_pricing: {
    short: "Reimburses providers at a percentage of Medicare reference rates.",
    detail:
      "Sets reimbursement levels for medical services based on a percentage of the Medicare fee schedule rather than negotiated network rates. Specific CPT codes and facilities can be excluded from reference-based pricing treatment.",
  },

  specialty_drug: {
    short: "Applies separate deductible terms for high-cost specialty medications.",
    detail:
      "Carves out specialty pharmaceutical claims into a separate deductible structure, isolating high-cost drug exposure from the standard specific deductible. Targeted NDC (National Drug Code) codes define which drugs are subject to this endorsement.",
  },

  exclusions: {
    short: "Excludes specific claim categories, conditions, or member groups from coverage.",
    detail:
      "Defines exclusions from stop-loss coverage including cell and gene therapy (CGT), late entrants to the plan, and retirees (with configurable age ranges). Specific ICD diagnostic codes can also be excluded from covered claims.",
  },

  domestic_reimbursement_adjustments: {
    short: "Adjusts reimbursement rates for claims at specific domestic facility types.",
    detail:
      "Applies a percentage adjustment to reimbursement for claims originating from specific domestic places of service. Requires a documented reason for the adjustment. Targeted POS (Place of Service) codes define which facilities are affected alongside specified provider locations.",
  },
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/** Get the description object for an endorsement type */
export const getEndorsementDescription = (type: EndorsementType): EndorsementDescription =>
  ENDORSEMENT_DESCRIPTIONS[type];

/** Get just the short description */
export const getShortDescription = (type: EndorsementType): string => ENDORSEMENT_DESCRIPTIONS[type].short;

/** Get just the detail description */
export const getDetailDescription = (type: EndorsementType): string => ENDORSEMENT_DESCRIPTIONS[type].detail;
